// ===== Binance public market-data feed for crypto symbols (forex stays simulated) =====
import { CRYPTO, barStart, getState, hooks, setLiveQuote, setLiveOpen, setRealBars, stopLive, log, emit } from "./store";

const WS_HOSTS = ["wss://data-stream.binance.vision", "wss://stream.binance.com:9443", "wss://stream.binance.us:9443"];
const REST_HOSTS = ["https://data-api.binance.vision", "https://api.binance.com", "https://api.binance.us"];
const INTERVAL = { M1: "1m", M5: "5m", M15: "15m", M30: "30m", H1: "1h", H4: "4h", D1: "1d", W1: "1w", MN: "1M" };
const BY_PAIR = Object.fromEntries(Object.entries(CRYPTO).map(([k, v]) => [v.toLowerCase(), k]));

let ws = null, hostIdx = 0, retry = 0, timer = null, active = false;
const loaded = new Set();

const setStatus = (s) => { getState().feedStatus = s; emit(); };

// Real candle history from REST for every timeframe (Binance 1w/1M match Monday/calendar-month bars)
const loadKlines = async (sym, tf) => {
  const key = `${sym}|${tf}`;
  if (!active || !CRYPTO[sym] || !INTERVAL[tf] || loaded.has(key)) return;
  loaded.add(key);
  for (const host of REST_HOSTS) {
    try {
      const r = await fetch(`${host}/api/v3/klines?symbol=${CRYPTO[sym]}&interval=${INTERVAL[tf]}&limit=1000`);
      if (!r.ok) continue;
      const rows = await r.json();
      if (!active || !rows.length) return;
      const bars = rows.map((k) => ({ t: barStart(tf, k[0]), o: +k[1], h: +k[2], l: +k[3], c: +k[4], v: +k[8] }));
      setRealBars(sym, tf, bars);
      return;
    } catch (e) { /* try next host */ }
  }
  loaded.delete(key);
};

const connect = () => {
  const streams = Object.values(CRYPTO).flatMap((p) => [`${p.toLowerCase()}@bookTicker`, `${p.toLowerCase()}@miniTicker`]).join("/");
  const host = WS_HOSTS[hostIdx % WS_HOSTS.length];
  setStatus("connecting");
  ws = new WebSocket(`${host}/stream?streams=${streams}`);
  ws.onopen = () => { retry = 0; setStatus("live"); log(`live crypto feed connected (${host.replace("wss://", "")})`, "Network"); };
  ws.onmessage = (e) => {
    const { stream, data } = JSON.parse(e.data);
    const sym = BY_PAIR[stream.split("@")[0]];
    if (!sym) return;
    if (stream.endsWith("@bookTicker")) setLiveQuote(sym, +data.b, +data.a);
    else setLiveOpen(sym, +data.o, +data.h, +data.l);
  };
  ws.onerror = () => ws && ws.close();
  ws.onclose = () => {
    ws = null;
    if (!active) return;
    hostIdx++; retry++;
    setStatus("reconnecting");
    timer = setTimeout(connect, retry < WS_HOSTS.length ? 200 : Math.min(10000, 1000 * 2 ** Math.min(retry, 4)));
  };
};

export const startFeed = () => {
  if (active) return;
  active = true;
  hooks.seriesCreated = loadKlines;
  connect();
  getState().charts.forEach((c) => loadKlines(c.symbol, c.tf));
};

export const stopFeed = () => {
  active = false;
  hooks.seriesCreated = null;
  clearTimeout(timer);
  if (ws) ws.close();
  loaded.clear();
  stopLive();
  setStatus("off");
  log("live crypto feed disconnected, crypto symbols simulated", "Network");
};

// Live partial order book (top 20 levels, 100ms) for one pair; returns a close function
export const openDepth = (pair, onBook, onStatus) => {
  let sock = null, idx = 0, closed = false, latest = null, t = null;
  const iv = setInterval(() => { if (latest) { onBook(latest); latest = null; } }, 250);
  const go = () => {
    const host = WS_HOSTS[idx % WS_HOSTS.length];
    onStatus("connecting");
    sock = new WebSocket(`${host}/ws/${pair.toLowerCase()}@depth20@100ms`);
    sock.onopen = () => onStatus("live");
    sock.onmessage = (e) => {
      const d = JSON.parse(e.data);
      latest = { bids: d.bids.map(([p, q]) => [+p, +q]), asks: d.asks.map(([p, q]) => [+p, +q]) };
    };
    sock.onerror = () => sock && sock.close();
    sock.onclose = () => {
      if (closed) return;
      idx++;
      onStatus("reconnecting");
      t = setTimeout(go, idx < WS_HOSTS.length ? 200 : 3000);
    };
  };
  go();
  return () => { closed = true; clearInterval(iv); clearTimeout(t); if (sock) sock.close(); };
};
